import { Badge } from "./ui/Badge";

/**
 * ============================================================================
 * A BOT'S TOOL-LOOP TRANSCRIPT, RENDERED
 * ============================================================================
 *
 * The one renderer behind both surfaces that show a transcript: the modal on
 * an individual bot reply (`BotTranscriptButton`) and the group's recent
 * attempts on the bot admin page. Extracted when the second one appeared —
 * the admin page had the `<pre>` inline.
 *
 * THE WIRE SHAPE IS `unknown`, DELIBERATELY, AND THAT DECIDES HOW THIS IS
 * WRITTEN. `GroupBotTrace.transcript` is the backend's raw `ChatMessage[]`
 * dumped through with no schema of its own, because it is a debug log rather
 * than a contract (see `backend/src/db/schema/bot-traces.ts`). So this narrows
 * it defensively and FALLS BACK TO THE RAW JSON the moment the shape is not
 * what it expects — a transcript the loop shape has moved on from is still
 * readable as a dump, which is strictly what the admin page showed before.
 * Never throw here: this is the surface a person opens *because* something
 * looks wrong.
 */

interface ToolCall {
  id?: unknown;
  name?: unknown;
  arguments?: unknown;
}

interface Turn {
  role: string;
  content: string | null;
  toolCalls: { name: string; arguments: string }[];
  toolCallId: string | null;
}

function asTurns(transcript: unknown): Turn[] | null {
  if (!Array.isArray(transcript) || transcript.length === 0) return null;
  const turns: Turn[] = [];
  for (const raw of transcript) {
    if (typeof raw !== "object" || raw === null) return null;
    const m = raw as Record<string, unknown>;
    if (typeof m.role !== "string") return null;
    const calls = Array.isArray(m.toolCalls) ? (m.toolCalls as ToolCall[]) : [];
    turns.push({
      role: m.role,
      content: typeof m.content === "string" ? m.content : null,
      toolCalls: calls.map((c) => ({
        name: typeof c.name === "string" ? c.name : "?",
        arguments: typeof c.arguments === "string" ? c.arguments : "",
      })),
      toolCallId: typeof m.toolCallId === "string" ? m.toolCallId : null,
    });
  }
  return turns;
}

/** The four roles the loop emits, in Danish. An unknown one prints as itself. */
const ROLE_LABEL: Record<string, string> = {
  system: "Instruks",
  user: "Samtalen indtil nu",
  assistant: "Botten",
  tool: "Værktøjssvar",
};

/**
 * Pretty-print a tool call's arguments, which arrive as RAW, UNPARSED JSON —
 * the model wrote them and nothing has validated them at this point, so a
 * string that will not parse is a real and interesting case. Show it as it
 * came rather than hiding it.
 */
function formatArguments(raw: string): string {
  if (raw.trim() === "") return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function BotTranscript({ transcript }: { transcript: unknown }) {
  const turns = asTurns(transcript);

  if (!turns) {
    return (
      <pre className="text-xs bg-surface-2 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
        {JSON.stringify(transcript, null, 2)}
      </pre>
    );
  }

  return (
    <ol className="space-y-3">
      {turns.map((turn, i) => (
        // The index IS the identity here: a transcript is a fixed, ordered
        // array that never reorders or grows once written.
        <li key={i} className="text-sm">
          <div className="flex items-center gap-2 mb-1">
            <Badge
              tone={turn.role === "assistant" ? "primary" : "neutral"}
              className="px-1.5 text-[10px] leading-none"
            >
              {ROLE_LABEL[turn.role] ?? turn.role}
            </Badge>
            {turn.toolCallId && (
              <span className="text-[10px] text-subtle-foreground font-mono truncate">
                {turn.toolCallId}
              </span>
            )}
          </div>

          {turn.content && (
            <p className="whitespace-pre-wrap break-words text-foreground">
              {turn.content}
            </p>
          )}

          {turn.toolCalls.map((call, j) => (
            <div key={j} className="mt-1.5">
              <p className="text-xs text-muted-foreground">
                Kaldte værktøjet{" "}
                <span className="font-mono text-foreground">{call.name}</span>
              </p>
              {formatArguments(call.arguments) !== "" && (
                <pre className="mt-1 text-xs bg-surface-2 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-words">
                  {formatArguments(call.arguments)}
                </pre>
              )}
            </div>
          ))}

          {/* An assistant turn that said nothing and called nothing is what
              a loop exhaustion looks like from here — say so rather than
              rendering an empty row the reader has to interpret. */}
          {!turn.content && turn.toolCalls.length === 0 && (
            <p className="text-xs text-subtle-foreground italic">Tomt svar</p>
          )}
        </li>
      ))}
    </ol>
  );
}
